import { useEffect, useState } from "react";

// Starts the next load before the reader reaches the end of what is loaded.
const LOOKAHEAD_MARGIN = "0px 0px 400px 0px";

/**
 * Loads the next chunk of an infinite list when a sentinel element placed
 * after the list nears the viewport.
 *
 * Returns a callback ref for the sentinel. `loadedCount` is the number of
 * chunks loaded so far; the observer is rebuilt when it changes, because an
 * observer reports only transitions, and a chunk too short to push the
 * sentinel off screen would otherwise never trigger the next load.
 *
 * A failed load stops the observer so it does not retry in a loop; the caller
 * offers a retry instead.
 */
export function useInfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  isError,
  fetchNextPage,
  loadedCount,
}) {
  const [sentinel, setSentinel] = useState(null);

  const canLoad = Boolean(hasNextPage) && !isFetchingNextPage && !isError;

  useEffect(() => {
    if (!sentinel || !canLoad) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: LOOKAHEAD_MARGIN }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, canLoad, loadedCount, fetchNextPage]);

  return setSentinel;
}
